import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { toneOf, isRunning, type State } from './status';
import { IconPause, IconPlay, IconStop } from '../ui/icons';
import { createPortal } from 'react-dom';
import { K } from '../kabinet/atoms';

/**
 * Управление кампанией: пуск-пауза и тумблер сервиса.
 *
 * ОДИН НАБОР НА ДВА ЭКРАНА. Те же две кнопки стоят и в списке, и внутри
 * карточки: две копии разошлись бы в поведении, а разойдясь — научили бы
 * человека не доверять ни одной. Из списка ими пользуются чаще: сорок
 * кампаний, и заходить в каждую, чтобы нажать «пауза», никто не станет.
 *
 * КНОПКА СЛЕДУЕТ ДЕЙСТВИТЕЛЬНОМУ СОСТОЯНИЮ, А НЕ СТАТУСУ WILDBERRIES.
 * Это была ошибка первой редакции, и вот в чём она: при холостом ходе
 * команда в WB не уходит, статус там остаётся «активна», а наше удержание
 * уже стоит. Кампания на экране «остановлена вручную» — и кнопка
 * предлагает остановить её ещё раз. Возобновить было нечем.
 *
 * Поэтому решает `stateOf`: он смотрит и на удержания, и на статус WB,
 * и «стоит» у него означает «стоит по любой причине».
 *
 * КНОПКИ ВНУТРИ ССЫЛКИ. Строка списка — ссылка на карточку, и нажатие
 * на кнопку внутри неё по умолчанию уводит со страницы. Останавливаем
 * всплытие и переход руками.
 */

export interface Controlled {
  id: string;
  managed: boolean;
  name?: string | null;
}

/**
 * Действие над кампанией — одно на все кнопки.
 *
 * ВЫНЕСЕНО В ОБЩЕЕ МЕСТО, ЧТОБЫ НЕ ЗАВЕСТИ ВТОРОЙ СПИСОК СБРОСА. Кнопка
 * завершения переехала из этого ряда под название кампании, и вместе
 * с ней уехало бы и обращение к серверу — со своим набором ключей,
 * которые надо обновить после. Разойдись эти наборы, и после завершения
 * список бы обновился, а плитки над ним — нет: экран показал бы два
 * разных числа об одном и том же. Эту беду мы уже чинили.
 */
export type CampaignAction = 'pause' | 'start' | 'stop' | 'manage' | 'unmanage';

export function useCampaignAction(base: string, id: string, onDone: (text: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (what: CampaignAction) =>
      what === 'manage' || what === 'unmanage'
        ? api<{ message: string }>(`${base}/campaigns/${id}/managed`, {
            method: 'PUT',
            body: { managed: what === 'manage' },
          })
        : api<{ message: string }>(`${base}/campaigns/${id}/${what}`, {
            method: 'POST',
            body: {},
          }),
    onSuccess: async (r) => {
      onDone(r.message);
      // ПЛИТКИ СБРАСЫВАЮТСЯ ВМЕСТЕ СО СПИСКОМ, и это не мелочь. Они
      // считают ровно то же самое — сколько работает, сколько стоит, —
      // и, обновись список без них, экран снова показал бы два разных
      // числа об одном и том же.
      for (const key of ['campaigns', 'overview', 'campaign', 'holds', 'schedule']) {
        await qc.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (e) => onDone(e instanceof Error ? e.message : 'Не вышло'),
  });
}

/** Нажатие внутри ссылки не должно уводить со страницы. */
function halt(e: React.MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
}

export function Controls({
  base,
  campaign,
  state,
  onDone,
  compact,
  withFinish = true,
  где = 'список',
  сЛампой = true,
}: {
  base: string;
  campaign: Controlled;
  state: State;
  onDone: (text: string) => void;
  compact?: boolean;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ГДЕ СТОИТ — ТАК И ЗОВЁТСЯ.
   *
   * Эталон даёт одному и тому же органу разные имена в зависимости
   * от экрана: в списке кампаний тумблер зовётся `тумблер-показов`,
   * на карточке — `тумблер-кампании`; кнопка — `кнопка-завершить`
   * и `кнопка-завершить-кампанию`.
   *
   * Это не прихоть дизайнера, а условие сверки: замер идёт по имени,
   * и одно имя на двух экранах означало бы, что расхождение на одном
   * из них перекрывается другим. Назвать оба одинаково у нас —
   * значит потерять половину сверки молча.
   * ═══════════════════════════════════════════════════════════════════
   */
  где?: 'список' | 'карточка';
  /**
   * Рисовать ли здесь же кнопку завершения.
   *
   * В ТАБЛИЦЕ — НЕТ, И ЭТО ПРОСЬБА ВЛАДЕЛЬЦА. Стоя в одном ряду с пуском
   * и тумблером, слово «Завершить» растягивало верхнюю строку каждой
   * кампании на треть и отодвигало название — то, чем строка называется
   * и по чему её ищут глазами. Внизу, под названием, она занимает
   * пустовавшее место и никого не двигает.
   */
  withFinish?: boolean;
  /**
   * Рисовать ли лампу «пуск/пауза».
   *
   * НА КАРТОЧКЕ ТЕЛЕФОНА — НЕТ: в эталоне на 390 у карточки один орган,
   * тумблер. Довод — в `campaign-cards.tsx`, там же, где решение видно.
   */
  сЛампой?: boolean;
}) {
  const run = useCampaignAction(base, campaign.id, onDone);

  const stop = halt;

  const running = isRunning(state);
  const dead = !state.live;
  const size = compact ? 22 : 26;

  return (
    /* ═══════════════════════════════════════════════════════════════
       ЧТО КОМАНДА ПОШЛА — ГОВОРИТ САМ ОРГАН, А НЕ ЗНАК РЯДОМ С НИМ.

       09.09.2026 владелец: «при нажатии на кнопки, тумблеры вылазит
       чёрная точка, её вообще не должно быть».

       ТОЧКА ЗДЕСЬ БЫЛА, И ДВАЖДЫ ЧИНИЛАСЬ ВМЕСТО ТОГО, ЧТОБЫ УЙТИ.
       Заведена, потому что команда в Wildberries идёт до минуты и всё
       это время не видно, пошло ли нажатие. 01.09 она двигала строку —
       вынес её из потока. Дёрганье ушло, а сама точка осталась: чёрное
       пятно шесть на шесть, вылезающее из угла нажатой кнопки. Владелец
       смотрит на неё третью неделю, и он прав — это заплатка, а не вид.

       ПРИЗНАК НЕ ПОТЕРЯН, А ПЕРЕЕХАЛ НА ТО, ЧТО НАЖАЛИ. Пока команда
       идёт, пульсирует сама кнопка (и сам тумблер) — тот орган, на
       который человек в этот миг и смотрит. Ноль лишних узлов, ноль
       движения соседей и нечему вылезать из угла.

       ПОЧЕМУ НЕ ХВАТАЕТ ОДНОГО `disabled`. Он даёт бледность, и только
       её: неотличимо от «кнопка тут просто неактивна». Пульс — это
       движение, и он читается как «идёт», а не как «нельзя».
       ═══════════════════════════════════════════════════════════════ */
    <span className="flex items-center gap-2">
      {/* ОДНА КНОПКА, А НЕ ДВЕ. У кампании одно состояние, и две кнопки
          заставляли бы гадать, какая из них сейчас нужна.

          СВЕТОФОР НА САМОЙ КНОПКЕ: ЦВЕТ — СОСТОЯНИЕ, ЗНАЧОК — ДЕЙСТВИЕ.
          Зелёная — кампания идёт; на ней две палочки, потому что нажатие
          её остановит. Оранжевая — стоит; на ней стрелка, потому что
          нажатие её запустит. Нажал зелёную — стала оранжевой, и наоборот.

          ЦВЕТ БЕРЁТСЯ ИЗ ТОГО ЖЕ `colourOf`, ЧТО И ПОЛОСА СЛЕВА, —
          не «такой же», а буквально тот же. Требование владельца
          дословно: «если кнопка оранжевым, значит и полоска тоже
          оранжевым». Два похожих правила однажды разойдутся; одно
          не может.

          ЧЕРЕЗ ЭТО МЕСТО ПРОШЛИ ТРИЖДЫ, и каждый поворот стоит помнить.
          Сперва кнопка была зелёной только у работающей, а остановленная
          белой — колонка читалась наоборот, единственный цвет стоял
          рядом со значком «пауза». Затем цвет убрали вовсе — и потеряли
          сводку: по колонке нельзя было понять ничего. Затем вернули
          двумя своими цветами — и они разошлись с полосой, у которой
          цветов было четыре. Свело всё одно: единственный источник
          цвета на строку.

          Завершённые — серые: у них нет ни состояния, которое можно
          изменить, ни действия. */}
      {/* ═══════════════════════════════════════════════════════════════
          ЛАМПА — ПУСК И ПАУЗА ПОКАЗОВ. ТУМБЛЕР — УПРАВЛЕНИЕ БИДРОЙ.

          ═══════════════════════════════════════════════════════════════
          ЭТО РЕШЕНИЕ ВЛАДЕЛЬЦА, И ОНО ОТМЕНЯЕТ ОТВЕТ ДИЗАЙНЕРА К-104.

          27.08 по ответу К-104 органы поменяли местами: лампа стала
          указателем «Показы: работает», тумблер — выключателем показов,
          а управление уехало в плитку на карточке кампании.

          Владелец открыл выкаченный кабинет и сказал прямо: «показывает,
          что 2 из 50 кампаний подключено, и это реально верно, а вот
          тумблеры включены у многих кампаний — это неправильное
          отображение… для этого и есть тумблеры: если они зелёные
          (включены), значит включено управление».

          И он прав по устройству экрана. Над таблицей стоит «Кампании
          2 из 50» — это СЧЁТ КАМПАНИЙ ПОД УПРАВЛЕНИЕМ. Если самый
          крупный зелёный орган строки означает не то, что считает
          число над ней, экран противоречит сам себе: пятьдесят зелёных
          тумблеров под надписью «2 из 50».

          В эталоне признака управления в строке НЕТ ВОВСЕ
          (`строка-кампании` содержит `тумблер-показов`, `имя-кампании`,
          `метка-группы`, «Завершить» и «Разбор» — и всё), а найти две
          управляемые из пятидесяти можно было бы только открыв
          пятьдесят карточек. Вопрос дизайнеру — К-139.

          ПОКАЗЫ ПРИ ЭТОМ НЕ ПОТЕРЯНЫ: ими правит лампа, как и до 27.08.
          Цвет её — тот же `toneOf(state.health)`, что у полосы слева:
          не «такой же», а буквально тот же. Требование владельца
          дословно: «если кнопка оранжевым, значит и полоска тоже
          оранжевым».
          ═══════════════════════════════════════════════════════════ */}
      {сЛампой && (
      <button
        disabled={run.isPending || dead}
        /* Подпись говорит, ЧТО СЛУЧИТСЯ при нажатии, а состояние
           словами — во второй половине: «Показы идут — нажатие
           остановит». Указатель без нажатия был бы честнее, но
           показами надо править, а тумблер занят управлением. */
        title={
          dead
            ? 'Кампания завершена'
            : running
              ? `Показы: ${state.text} — нажатие остановит`
              : `Показы: ${state.text} — нажатие запустит`
        }
        aria-label={running ? 'Остановить показы' : 'Запустить показы'}
        onClick={(e) => {
          stop(e);
          run.mutate(running ? 'pause' : 'start');
        }}
        className={`rk-light rk-light-${toneOf(state.health)} disabled:opacity-40` +
          (run.isPending ? ' animate-pulse' : '')}
      >
        {running ? <IconPause /> : <IconPlay />}
      </button>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          ТУМБЛЕР — УПРАВЛЕНИЕ БИДРОЙ. Довод целиком — у лампы выше.

          ЗЕЛЁНЫЙ ЗНАЧИТ «БИДРА ПРАВИТ СТАВКИ ЭТОЙ КАМПАНИИ», и ровно
          это считает надпись «Кампании 2 из 50» над таблицей. Выключил
          тумблер — кампания осталась работать у Wildberries ровно как
          была, только ставки больше не двигаются нами, и в тарифе
          освободилось место.

          ЗАВЕРШЁННУЮ НЕ ВЗЯТЬ ПОД УПРАВЛЕНИЕ: править в ней нечего,
          и тумблер у неё выключен намертво.
          ═══════════════════════════════════════════════════════════ */}
      <button
        disabled={run.isPending || dead}
        data-d={где === 'карточка' ? 'тумблер-кампании' : 'тумблер-показов'}
        role="switch"
        aria-checked={campaign.managed}
        aria-label={campaign.managed ? 'Снять с управления' : 'Взять под управление'}
        title={
          dead
            ? 'Кампания завершена — управлять нечем'
            : campaign.managed
              ? 'БИДРА правит ставки — нажатие снимет с управления'
              : 'Ставки не меняем — нажатие возьмёт под управление'
        }
        onClick={(e) => {
          stop(e);
          run.mutate(campaign.managed ? 'unmanage' : 'manage');
        }}
        /* Размер и радиус — частицей из эталона; цвет включённого
           состояния поверх неё: он про состояние, а не про вид. */
        className={
          (где === 'карточка' ? K.тумблерКампании : K.тумблерПоказов) +
          ' relative transition-colors disabled:opacity-40' +
          (run.isPending ? ' animate-pulse' : '')
        }
        style={{ background: campaign.managed ? 'var(--good)' : 'var(--line-strong)' }}
      >
        <span
          className="absolute rounded-full bg-white transition-all"
          style={{
            width: size - 6,
            height: size - 6,
            top: 3,
            left: campaign.managed ? size * 1.7 - (size - 3) : 3,
          }}
        />
      </button>

      {/* ═══════════════════════════════════════════════════════════════
          «ЗАВЕРШИТЬ» — ПОСЛЕДНЕЙ, ЗА ТУМБЛЕРОМ. Так в эталоне: в шапке
          кампании ряд читается «тумблер · Завершить». У нас кнопка
          стояла МЕЖДУ лампой и тумблером, и необратимое действие
          оказывалось посреди обратимых — там, куда рука идёт мимо.
          ═══════════════════════════════════════════════════════════ */}
      {withFinish && !dead && (
        <FinishCampaign base={base} campaign={campaign} state={state} onDone={onDone} где={где} />
      )}
    </span>
  );
}

/**
 * Завершение кампании — единственная необратимая кнопка сервиса.
 *
 * ВЫГЛЯДЕТЬ ОНА ОБЯЗАНА ИНАЧЕ, ЧЕМ ОБРАТИМЫЕ. Пауза и пуск — цветные
 * кружки; завершение спрятано за словом, обведено рамкой и спрашивает
 * подтверждение. Не «на всякий случай», а по существу: завершённую
 * кампанию Wildberries не запустит уже никогда — ни мы, ни человек
 * из их кабинета.
 *
 * ОТДЕЛЬНЫМ УЗЛОМ, ЧТОБЫ СТОЯТЬ ГДЕ НУЖНО. В таблице она уехала вниз,
 * под название кампании: в верхнем ряду слово «Завершить» отодвигало
 * название вправо на треть колонки. В карточке кампании она осталась
 * в общем ряду — там места довольно.
 */
export function FinishCampaign({
  base,
  campaign,
  state,
  onDone,
  className,
  где = 'список',
}: {
  base: string;
  campaign: Controlled;
  state: State;
  onDone: (text: string) => void;
  className?: string;
  /** Довод об именах — у `Controls` выше. */
  где?: 'список' | 'карточка';
}) {
  const run = useCampaignAction(base, campaign.id, onDone);
  const [confirmStop, setConfirmStop] = useState(false);

  // Завершённую не завершить дважды: у неё нет ни состояния, которое
  // можно изменить, ни действия.
  if (!state.live) return null;

  return (
    <>
      <button
        disabled={run.isPending}
        data-d={где === 'карточка' ? 'кнопка-завершить-кампанию' : 'кнопка-завершить'}
        className={
          (где === 'карточка' ? K.кнопкаЗавершитьКампанию : K.кнопкаЗавершить) +
          /* НАВЕДЕНИЕ КРАСНОЕ И С ПОДЛОЖКОЙ — так в эталоне 07.09.2026:
             рамка и знак `--bad-ink` на `--bad-wash`. Прежде подложки
             не было, и значок в строке при наведении почти не менялся. */
          ' transition-colors hover:border-[var(--bad)] hover:text-[var(--bad-ink)]' +
          ' hover:bg-[var(--bad-wash)] ' +
          (className ?? '')
        }
        title={
          где === 'карточка'
            ? 'Завершить кампанию насовсем'
            : 'Завершить кампанию — насовсем: в Wildberries тоже'
        }
        aria-label="Завершить кампанию"
        onClick={(e) => {
          halt(e);
          setConfirmStop(true);
        }}
      >
        {/* КНОПКОЙ, А НЕ СЛОВОМ. Слово в строке читается как подпись
            и не нажимается: владелец не нашёл его вовсе. Рамка и значок
            говорят «сюда можно нажать» до того, как прочитано само
            слово — а необратимое действие обязано быть ЗАМЕТНЫМ,
            и по той же причине не должно быть ярким. */}
        {/* ═══════════════════════════════════════════════════════════
            В СТРОКЕ — ЗНАЧОК БЕЗ СЛОВА, НА КАРТОЧКЕ — СО СЛОВОМ.

            Передача 07.09.2026, раздел 4. В ряду свойств кнопка
            терялась и читалась как ещё одна метка, хотя это
            необратимое действие: кампания завершается и в Wildberries.
            Теперь она стоит в левой группе управления третьей —
            после светофора состояния и тумблера показов, — потому
            что слева собраны органы управления показами, а завершение
            есть крайняя степень того же ряда. Справа в строке живут
            числа, кнопке там не место.

            Подпись ушла в подсказку: квадрат 26 × 26 со словом внутри
            не помещается, а ряд управления обязан быть узким.

            Полная кнопка со словом осталась там, где действие
            осознанное: в шапке экрана кампании и в полосе выделенного
            при массовом действии.
            ═══════════════════════════════════════════════════════ */}
        {где === 'карточка' ? (
          <>
            <IconStop size={9} /> Завершить
          </>
        ) : (
          <svg width="9" height="9" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <rect x="2" y="2" width="12" height="12" rx="2.4" />
          </svg>
        )}
      </button>

      {confirmStop && (
        <ConfirmStop
          name={campaign.name ?? String(campaign.id)}
          onCancel={() => setConfirmStop(false)}
          onYes={() => {
            setConfirmStop(false);
            run.mutate('stop');
          }}
        />
      )}
    </>
  );
}

/**
 * Подтверждение завершения.
 *
 * ОБЫЧНЫЙ `confirm` ЗДЕСЬ НЕ ГОДИТСЯ, и дело не в красоте. Системное
 * окно человек закрывает не читая — он видел их тысячу, и в девятистах
 * случаях там было «вы уверены?» без последствий. Здесь последствие
 * есть, и написать его надо так, чтобы прочли: не «вы уверены»,
 * а «вернуть будет нельзя».
 *
 * ОПАСНАЯ КНОПКА НЕ ПЕРВАЯ И НЕ ПОДСВЕЧЕНА. Первой стоит «Отмена»:
 * промах по случайному нажатию должен попадать в безопасное.
 */
function ConfirmStop({
  name,
  onCancel,
  onYes,
}: {
  name: string;
  onCancel: () => void;
  onYes: () => void;
}) {
  /* ═══════════════════════════════════════════════════════════════════
     РИСУЕТСЯ В КОРНЕ СТРАНИЦЫ, А НЕ В СТРОКЕ ТАБЛИЦЫ.

     Окно висело прямо там, где стоит кнопка «Завершить», — внутри
     клетки таблицы. Кегль оно при этом НАСЛЕДОВАЛО от таблицы: 13
     вместо эталонных 14, и весь текст окна выходил на ступень мельче
     всех прочих окон кабинета. Ровно это и намерила сверка.

     Заодно `position: fixed` внутри прокручиваемого предка — известная
     ловушка: стоит появиться `transform` где-нибудь выше, и «фиксация»
     начинает считаться от него, а не от экрана.
     ═══════════════════════════════════════════════════════════════ */
  return createPortal(
    /* ═══════════════════════════════════════════════════════════════
       ЗАТЕМНЕНИЕ — ТОЙ ЖЕ ЧАСТИЦЕЙ, ЧТО У ОСТАЛЬНЫХ ОКОН.

       Здесь оно было набрано руками: `items-center`, без полей,
       `z-50`. У эталона (`окно-завершения`) — `align-items:flex-start`,
       поля `--sp-8` сверху и `--sp-4` по бокам, `z-index: 900`
       и прокрутка. То есть окно завершения вставало по центру экрана,
       а все прочие окна кабинета — от верхнего поля: два окна одного
       кабинета открывались по-разному, и на невысоком экране
       центрированное упиралось в края без возможности прокрутить.

       `data-kab` обязателен: правило частицы записано как ПОТОМОК
       области (`[data-kab] .k-подложка-окна`), см. довод в `ui/modal`.
       ═══════════════════════════════════════════════════════════ */
    /* ОБЛАСТЬ И ЧАСТИЦА — НА РАЗНЫХ УЗЛАХ. Правило записано как
       `[data-kab] .k-подложка-окна`, то есть ПОТОМОК области: пока
       и то и другое висело на одном узле, правило не применялось
       вовсе (узел сам себе не потомок), и поля брались из общего
       `.rk-scrim` — 32 кругом вместо эталонных 32 сверху и 16
       по бокам. Тот же капкан описан в `ui/modal.tsx`. */
    <span data-kab style={{ display: 'contents' }}>
    <span
      data-d="окно-завершения"
      className={`rk-scrim ${K.подложкаОкна}`}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onCancel();
      }}
    >
      <span
        className="rk-panel flex max-w-md flex-col gap-3 p-4"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <b>Завершить «{name}»?</b>
        <span>
          Это необратимо. Завершённую кампанию Wildberries не запустит уже никогда — ни отсюда,
          ни из их кабинета. Накопленная статистика, ставки и состав больше не пригодятся.
        </span>
        <span className="rk-quiet">
          Если нужно просто остановить показы — закройте это окно и нажмите паузу: её можно снять
          в любой момент.
        </span>
        <span className="flex justify-end gap-2">
          <button className="rk-button-quiet" onClick={onCancel}>
            Отмена
          </button>
          <button className="rk-button-danger" onClick={onYes}>
            Завершить насовсем
          </button>
        </span>
      </span>
    </span>
    </span>,
    document.body,
  );
}
